/*
 * PageWright — persistent "already in your cart" state for add-to-cart controls.
 *
 * 2026-07-29, Paul: "we want the add to cart to change colour so you know you've already added
 * it." The theme had three ADDED states before this and every one of them was transient:
 *   - product-form.js sets `data-added` for the checkmark animation, then clears it
 *   - product-form.js showAlreadyInCart() flashes "Already in cart", then reverts
 *   - pw-page-cart.js sets `data-pw-added` on page tiles, then removes it after 2s
 * So scrolling back to a product, reloading, or returning later gave no signal at all.
 *
 * This is deliberately driven by the REAL CART (/cart.js), not by "did you click recently":
 * server cart state survives reloads, other tabs, and edits made from the cart drawer. A
 * timer-based flag would go on lying the moment any of those happened.
 *
 * It is additive — it never touches `data-added`. Those are two different statements:
 *   data-added        = "the click you just made worked"
 *   data-pw-in-cart   = "this is in your cart right now"
 *
 * Colour is not the only carrier: the control also gets an accessible title + aria-label
 * suffix, because colour alone fails WCAG 1.4.1.
 *
 * 2026-08-12, Paul (verbatim): "if you add any product to the cart the Add To Cart button turns
 * black but doesn't change text to Added." Colour + title/aria-label was the right call for
 * WCAG 1.4.1 (colour must never be the SOLE carrier) but it under-shot the actual ask — sighted
 * users never see a title/aria-label change, only assistive tech does. swapLabel() below now
 * ALSO changes the visible text, for every control shape this theme ships a known structure for
 * (see labelElFor). It reuses the exact same `pwDefaultText` cache attribute
 * assets/product-form.js's #getOriginalText already uses for the PDP's `.add-to-cart-text__content`
 * span — deliberately the SAME name, not a second independent cache, because both files can touch
 * that same span (this file for the persistent cart state, product-form.js for the transient
 * click animation) and must agree on what "the true original label" is regardless of which one
 * ran first or is mid-flight.
 */
(function () {
  'use strict';

  var MARK = 'data-pw-in-cart';
  var LABEL = 'In your cart';
  var OWNED_LABEL = 'Already in your library';

  /*
   * OWNERSHIP (Paul, 2026-07-29): "only the Add To Cart should turn black a) after you add it to
   * cart or b) you already own that book or image page (its in your MyLibrary)."
   *
   * (a) is the cart check below. (b) needs entitlements, which are behind an authenticated call —
   * so this is BEST-EFFORT BY DESIGN: it marks owned items only when a My Library token exists
   * (sessionStorage 'pw_library_token', the same key pw-library.js writes and pw-favorites.js
   * reads). An anonymous visitor sees no ownership state, because we genuinely cannot know.
   *
   * Deliberately fails toward "not owned": a missing/expired token or a failed request leaves
   * buttons in their normal state. Wrongly telling someone they already own a book would suppress
   * a real sale; wrongly omitting the badge just shows a normal buy button.
   *
   * Cached for the session so this costs one request, not one per page view.
   */
  /* Same literal PWAuth.TOKEN_KEY holds -- referenced directly so there's one real source of
   * truth once PWAuth has loaded (see layout/theme.liquid load order), literal kept as the
   * pre-load/failed-load fallback. */
  var TOKEN_KEY = (window.PWAuth && window.PWAuth.TOKEN_KEY) || 'pw_library_token';
  var OWNED_CACHE_KEY = 'pw_owned_handles';
  var BACKEND_URL = (window.PW_BACKEND_URL || '')  // set once by layout/theme.liquid from the
  // per-theme `backend_url` setting. Blank DISABLES backend calls rather than falling back
  // to production — a staging theme silently writing to prod data is the bug this replaces.

  /**
   * Every control that represents "add THIS variant", across PDP, grids, search and quick-add.
   *
   * `.pw-card-pill--add` matters and is easy to miss: the collection/search grid cards
   * (snippets/pw-card-ctas.liquid) render `<button type="submit" class="pw-card-pill--add">`
   * with NO name="add" — only the PDP button carries that. Selecting on name="add" alone meant
   * grid Add-to-Cart never got marked, which is exactly what Paul reported on 2026-07-29
   * ("works on a product page, but from menu > Books it does NOT change colour").
   */
  var CONTROL_SELECTOR = [
    'button[name="add"]',        // PDP + quick-add modal
    '.pw-card-pill--add',        // collection / search grid cards
    '[data-pw-add-page]',        // single-page tiles
    '[data-variant]',            // image-search tiles
    'input[name="id"]',          // hidden variant input -> resolved to its submit button
  ].join(', ');

  function controls() {
    return document.querySelectorAll(CONTROL_SELECTOR);
  }

  /** The variant id a control would post, wherever that lives in this theme's various markups. */
  function variantIdFor(el) {
    if (el.tagName === 'INPUT' && el.name === 'id') return String(el.value || '').trim();
    if (el.hasAttribute('data-variant')) return String(el.getAttribute('data-variant') || '').trim();
    // A submit button inside a product form: read the form's hidden variant input.
    var form = el.closest && el.closest('form');
    var input = form && form.querySelector('input[name="id"]');
    if (input) return String(input.value || '').trim();
    return '';
  }

  /*
   * PAGE IDENTITY (2026-07-30, audit P1-1).
   *
   * A variant id CANNOT identify a page. Every single-page add button sitewide carries the same
   * static tier-1 variant (pw-book-pages.js:85, pw-image-search.js:159) and the cart line moves to
   * a different tier variant once the basket passes 10 pages. pw-page-cart.js already solved this
   * for itself: it identifies a page by the line-item properties it posts, `_book_handle` + `Page`.
   * This mirrors that exactly, so the two modules agree on what "the same page" means.
   *
   * Returns '' for anything that is not a single-page add button, and those keep variant identity.
   */
  function pageKeyFor(el) {
    if (!el.hasAttribute || !el.hasAttribute('data-pw-add-page')) return '';
    var book = el.getAttribute('data-parent-handle') || el.getAttribute('data-book-handle') || '';
    var page = el.getAttribute('data-page-number');
    if (!book || page == null || page === '') return '';
    return 'page:' + book + ':' + String(page);
  }

  /** The same key, derived from a /cart.js line rather than from the DOM. */
  function pageKeyForCartItem(item) {
    var props = item && item.properties;
    if (!props) return '';
    var book = props._book_handle;
    var page = props.Page;
    if (!book || page == null || page === '') return '';
    return 'page:' + String(book) + ':' + String(page);
  }

  /**
   * The element we actually decorate — for a hidden input, that's its form's submit button.
   * Must NOT look only for button[name="add"]: grid cards' submit button has no name attribute
   * (see CONTROL_SELECTOR above), so that lookup returned null and the card was never marked.
   */
  function targetFor(el) {
    if (el.tagName !== 'INPUT') return el;
    var form = el.closest && el.closest('form');
    if (!form) return null;
    return (
      form.querySelector('button[name="add"]') ||
      form.querySelector('.pw-card-pill--add') ||
      form.querySelector('button[type="submit"]') ||
      null
    );
  }

  /**
   * The element that carries a control's VISIBLE label text, for the shapes this theme actually
   * ships. Returns null for anything else — text is then left alone and colour/title/aria-label
   * remain the only signal for that shape, rather than guessing at an unknown structure and
   * corrupting markup (an icon, a price) this file doesn't understand.
   *
   *   - PDP / quick-add `button[name="add"]`: nested `.add-to-cart-text__content` span (also
   *     touched by product-form.js's animateAddToCart() — see the shared `pwDefaultText` cache
   *     note above).
   *   - Collection/search grid card (`.pw-card-pill--add`): `.pw-card-pill__line1` — line2 is the
   *     price and must never be touched.
   *   - Single-page tiles (`[data-pw-add-page]`, pw-book-pages.js / pw-image-search.js): the
   *     button IS its own label, no nested span.
   *
   * @param {Element} target
   * @returns {Element|null}
   */
  function labelElFor(target) {
    if (!target || !target.querySelector) return null;
    var nested = target.querySelector('.add-to-cart-text__content');
    if (nested) return nested;
    var pill = target.querySelector('.pw-card-pill__line1');
    if (pill) return pill;
    if (target.hasAttribute && target.hasAttribute('data-pw-add-page')) return target;
    return null;
  }

  /**
   * Swaps (or restores) a control's VISIBLE label text, where we know how to find it.
   * Deliberately shares the `pwDefaultText` dataset key with assets/product-form.js's
   * #getOriginalText — see the header comment above for why a second, independently-named cache
   * would be unsafe.
   * @param {Element} target
   * @param {boolean} on
   * @param {string} labelText
   */
  function swapLabel(target, on, labelText) {
    var el = labelElFor(target);
    if (!el) return;
    if (on) {
      if (el.dataset.pwDefaultText === undefined) {
        el.dataset.pwDefaultText = el.textContent;
      }
      el.textContent = labelText;
    } else if (el.dataset.pwDefaultText !== undefined) {
      el.textContent = el.dataset.pwDefaultText;
    }
  }

  function mark(el, on, label) {
    var LABEL_TEXT = label || LABEL;
    if (!el) return;
    if (on) {
      if (el.hasAttribute(MARK)) return;
      el.setAttribute(MARK, '');
      // Colour is never the sole signal (WCAG 1.4.1).
      if (!el.getAttribute('title')) el.setAttribute('title', LABEL_TEXT);
      var current = el.getAttribute('aria-label');
      if (current && current.indexOf(LABEL_TEXT) === -1) el.setAttribute('aria-label', current + ' — ' + LABEL_TEXT);
      el.setAttribute('data-pw-in-cart-label', LABEL_TEXT);
      // 2026-08-12: the VISIBLE label too, not just colour + a11y attributes (Paul's bug report).
      swapLabel(el, true, LABEL_TEXT);
    } else {
      if (!el.hasAttribute(MARK)) return;
      el.removeAttribute(MARK);
      el.removeAttribute('data-pw-in-cart-label');
      if (el.getAttribute('title') === LABEL_TEXT) el.removeAttribute('title');
      var lbl = el.getAttribute('aria-label');
      if (lbl && lbl.indexOf(' — ' + LABEL_TEXT) !== -1) {
        el.setAttribute('aria-label', lbl.replace(' — ' + LABEL_TEXT, ''));
      }
      swapLabel(el, false, LABEL_TEXT);
    }
  }

  /** Product handle a control refers to: the card's own heart mount, else the PDP URL. */
  /*
   * The product handle a control refers to.
   *
   * 2026-07-30 re-audit: this used to hunt for a [data-pw-fav-handle] heart via
   * closest('...form').querySelector(...). In the shipped markup that NEVER works — the add
   * button lives inside {% form 'product' %} (snippets/pw-card-ctas.liquid) while the heart is a
   * SIBLING of the CTAs inside the card (blocks/_product-card-gallery.liquid, deliberately moved
   * out 2026-07-12). closest() stops at the FORM, the heart is outside it, the lookup returns
   * null, and the code fell through to the page URL — handing back the CURRENT PDP's handle for
   * every control on the page. Two live consequences:
   *
   *   - on the PDP of a book you own, every recommendation card was marked "Already in your
   *     library" for books never purchased. That suppresses a real sale, which the header of this
   *     file says must never happen.
   *   - on /collections and /search there is no /products/ segment, so the key was '' and
   *     ownership never rendered at all.
   *
   * Fixed by having the control carry its own identity: pw-card-ctas.liquid stamps
   * data-pw-handle="{{ product.handle }}" on every add button. Read that first, and only fall
   * back to the URL when the control genuinely has no handle of its own (the PDP's own primary
   * button in markup we do not render).
   */
  function handleFor(el) {
    if (el.hasAttribute && el.hasAttribute('data-pw-handle')) {
      var own = el.getAttribute('data-pw-handle');
      if (own) return own;
    }
    // A heart inside the SAME scope is still authoritative where one is genuinely reachable.
    var scope = el.closest && el.closest('product-card, .product-card, li, .pw-card, form');
    var fav = scope && scope.querySelector('[data-pw-fav-handle]');
    if (fav) return fav.getAttribute('data-pw-fav-handle');
    var m = /\/products\/([^/?#]+)/.exec(window.location.pathname);
    return m ? m[1] : '';
  }

  var ownedHandles = null;

  function loadOwned() {
    if (ownedHandles) return Promise.resolve(ownedHandles);
    var token;
    try {
      // 2026-08-13/14 login forensics, defect #2: sessionStorage-only read, destroyed on
      // browser close, on a script loaded site-wide. Delegates entirely to the shared
      // window.PWAuth (assets/pw-auth.js, loaded before this file) for the durable-storage
      // fallback — this file's OWN test guard (test_add_to_cart_in_cart_state_072900.py)
      // asserts cart-membership truth is never read from that other browser store, so the
      // fallback logic lives only in pw-auth.js, never duplicated here. Old sessionStorage-only
      // read kept as the one-line degrade path if the shared asset ever fails to load.
      token = window.PWAuth ? window.PWAuth.getToken() : sessionStorage.getItem(TOKEN_KEY);
      var cached = sessionStorage.getItem(OWNED_CACHE_KEY);
      if (cached) {
        ownedHandles = new Set(JSON.parse(cached));
        return Promise.resolve(ownedHandles);
      }
    } catch (e) {
      /* storage disabled → skip ownership entirely rather than guess */
    }
    if (!token) {
      ownedHandles = new Set();
      return Promise.resolve(ownedHandles);
    }
    return fetch(BACKEND_URL + '/api/library', {
      headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
      signal: AbortSignal.timeout(8000),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        // Sliding-renewal composition (pagewright PR #224): /api/library returns a fresh
        // renewedToken once a token crosses 50% of its life. This is the one call site that
        // fires on ordinary site browsing (not just My Library visits), so persisting it here
        // is what makes renewal actually keep a browsing customer signed in. No-op until #224
        // merges — data.renewedToken is simply absent until then.
        if (data && data.renewedToken && window.PWAuth) {
          window.PWAuth.storeToken(data.renewedToken);
        }
        var items = (data && (data.items || data.entitlements)) || [];
        var set = new Set();
        for (var i = 0; i < items.length; i++) {
          var it = items[i] || {};
          var h = it.bookHandle || it.book_handle || it.productHandle || it.product_handle || it.handle;
          if (!h) continue;
          /*
           * ASSET KIND GATE (2026-07-30, audit P1-1). Owning the eBook is not owning the
           * standalone single-page buy, and vice versa — different products, different prices.
           * Keying both on the bare handle marked one as owned whenever you held the other.
           * Page entitlements are stored under the same 'page:book:n' key the DOM and the cart
           * both produce, so all three agree.
           */
          var kind = it.assetKind || it.asset_kind;
          var pageNo = it.pageNumber != null ? it.pageNumber : it.page_number;
          if (kind === 'page') {
            if (pageNo != null && pageNo !== '') set.add('page:' + String(h) + ':' + String(pageNo));
          } else {
            set.add(String(h));
          }
        }
        ownedHandles = set;
        try { sessionStorage.setItem(OWNED_CACHE_KEY, JSON.stringify([...set])); } catch (e) { /* non-fatal */ }
        return set;
      })
      .catch(function () {
        ownedHandles = new Set(); // fail toward "not owned" — never suppress a real sale
        return ownedHandles;
      });
  }

  var syncing = false;

  function sync() {
    if (syncing) return Promise.resolve();
    syncing = true;
    return Promise.all([
      fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(
        function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        },
      ),
      loadOwned(),
    ])
      .then(function (both) {
        var cart = both[0];
        var owned = both[1] || new Set();
        var inCart = {};
        var items = (cart && cart.items) || [];
        for (var i = 0; i < items.length; i++) {
          var item = items[i];
          if (!item) continue;
          // A page line is indexed ONLY by its page key. Indexing it by variant too would
          // re-introduce the exact bug: every page shares the tier variant, so one page in the
          // cart would light up every page tile on the site.
          var ck = pageKeyForCartItem(item);
          if (ck) {
            inCart[ck] = true;
          } else if (item.variant_id != null) {
            inCart[String(item.variant_id)] = true;
          }
        }
        var els = controls();
        for (var j = 0; j < els.length; j++) {
          var el = els[j];
          var target = targetFor(el);
          if (!target) continue;
          // A single-page button is identified by book+page; everything else by variant.
          var pk = pageKeyFor(el);
          var id = pk || variantIdFor(el);
          if (!id) continue;
          var isInCart = inCart[id] === true;
          // Ownership is a second, independent reason to show the state — Paul's (b). Pages are
          // looked up under their page key, whole products under their handle, never crossed.
          var ownKey = pk || String(handleFor(el));
          var isOwned = !isInCart && owned.size > 0 && !!ownKey && owned.has(ownKey);
          mark(target, isInCart || isOwned, isOwned ? OWNED_LABEL : LABEL);
        }
      })
      .catch(function () {
        /* cart unreachable → leave the DOM exactly as-is; never degrade the buy path */
      })
      .finally(function () {
        syncing = false;
      });
  }

  /**
   * Re-sync after anything that could mutate the cart. There is no single reliable cart event in
   * this theme (Horizon dispatches its own, pw-page-cart.js posts /cart/add.js directly), so we
   * listen broadly and cheaply rather than coupling to one implementation.
   */
  function watch() {
    ['cart:updated', 'cart:refresh', 'variant:update'].forEach(function (name) {
      document.addEventListener(name, function () {
        setTimeout(sync, 150);
      });
    });
    /*
     * Deterministic: re-sync when a cart MUTATION RESPONSE lands, not on a guessed delay.
     *
     * The first version of this used a single setTimeout(sync, 600) on click. Real-browser
     * testing on staging (2026-07-29) caught it racing: the item was genuinely added (cart went
     * 5 -> 6) but the button stayed unmarked, because the 600ms sample happened before Shopify
     * finished writing the cart. A single fixed delay is a coin flip on a slow network — and the
     * unit test could not see it, because the bug only exists in real request timing.
     *
     * Wrapping fetch means we sync exactly when the mutation actually completed, regardless of
     * which code path issued it (Horizon's own components, pw-page-cart.js, quick-add).
     */
    var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?/;
    if (window.fetch && !window.fetch.__pwInCartWrapped) {
      var nativeFetch = window.fetch.bind(window);
      var wrapped = function (input, init) {
        var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
        return nativeFetch(input, init).then(function (resp) {
          if (CART_MUTATION.test(String(url))) setTimeout(sync, 60);
          return resp;
        });
      };
      wrapped.__pwInCartWrapped = true;
      window.fetch = wrapped;
    }
    // XHR fallback — some Shopify/theme paths still use XMLHttpRequest rather than fetch.
    if (window.XMLHttpRequest && !XMLHttpRequest.prototype.__pwInCartWrapped) {
      var nativeSend = XMLHttpRequest.prototype.send;
      var nativeOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__pwUrl = url;
        return nativeOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function () {
        var self = this;
        this.addEventListener('load', function () {
          if (CART_MUTATION.test(String(self.__pwUrl || ''))) setTimeout(sync, 60);
        });
        return nativeSend.apply(this, arguments);
      };
      XMLHttpRequest.prototype.__pwInCartWrapped = true;
    }
    // Belt-and-braces click fallback, in case a path bypasses both of the above.
    document.addEventListener(
      'click',
      function (e) {
        var t =
          e.target &&
          e.target.closest &&
          e.target.closest('button[name="add"], .pw-card-pill--add, [data-pw-add-page]');
        if (t) {
          setTimeout(sync, 700);
          setTimeout(sync, 1800);
        }
      },
      true,
    );
    // Returning to the tab, or bfcache restore, after editing the cart elsewhere.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) sync();
    });
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) sync();
    });
    document.addEventListener('shopify:section:load', function () {
      sync();
    });
  }

  function init() {
    sync();
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
